import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { threadCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Modal } from '@/ui/Modal/Modal'
import { promoteResource, resourcesFor } from '../../api/resources'
import { kindDisplay } from '../../constants/resourceKinds'
import { useResourceKinds } from '../../hooks/useResourceKinds'
import type { TopicResource } from '../../types/api'
import styles from './ThreadResolvePrompt.module.css'

type RowOutcome = 'kept' | 'moved' | 'archived' | 'error'

type RowAction = 'keep' | 'move' | 'archive'

interface Props {
  topicId: string
  rows: TopicResource[]
  onClose: () => void
}

/**
 * Post-resolve prompt (redesign §4/Phase 4): a resolved thread's
 * scoped resources would be easy to lose track of — for each one, keep
 * it thread-scoped, promote it to the topic pool, or archive (delete)
 * it. Actions apply immediately; the modal closes when every row has an
 * outcome.
 */
export function ThreadResolvePrompt({ topicId, rows, onClose }: Props) {
  const qc = useQueryClient()
  const [outcomes, setOutcomes] = useState<Record<string, RowOutcome>>({})
  // The row and action pending: only the clicked button shows it.
  const [busy, setBusy] = useState<{ id: string; action: RowAction } | null>(
    null,
  )
  const { data: kinds } = useResourceKinds()
  const [lastError, setLastError] = useState<string | null>(null)

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['resource'] })
    void qc.invalidateQueries({ queryKey: ['resources'] })
    void qc.invalidateQueries({ queryKey: ['thread-resources'] })
  }

  async function act(row: TopicResource, action: RowAction) {
    setBusy({ id: row.id, action })
    setLastError(null)
    try {
      if (action === 'move') {
        await promoteResource(topicId, row.id, 'topic')
      } else if (action === 'archive') {
        await resourcesFor({ kind: 'topic', id: topicId }).delete(row.id)
      }
      setOutcomes((o) => ({
        ...o,
        [row.id]:
          action === 'keep' ? 'kept' : action === 'move' ? 'moved' : 'archived',
      }))
      refresh()
    } catch (err) {
      setLastError(
        err instanceof Error
          ? err.message
          : action === 'move'
            ? threadCopy.resolve.moveFailed(row.name || row.kind)
            : threadCopy.resolve.archiveFailed(row.name || row.kind),
      )
      setOutcomes((o) => ({ ...o, [row.id]: 'error' }))
    } finally {
      setBusy(null)
    }
  }

  const pending = rows.filter((r) => !outcomes[r.id])

  return (
    <Modal
      open
      onClose={onClose}
      title={threadCopy.resolve.title}
      footer={
        <Button variant="primary" ground="surface" onClick={onClose}>
          {pending.length === 0
            ? threadCopy.resolve.done
            : threadCopy.resolve.keepRemaining}
        </Button>
      }
    >
      <Stack gap="space-12">
        {lastError && <Alert tone="danger">{lastError}</Alert>}
        <Text as="p" tone="text-sub">
          {threadCopy.resolve.body}
        </Text>
        <ul className={styles.rows}>
          {rows.map((r) => {
            const outcome = outcomes[r.id]
            const kind = kindDisplay(r.kind, kinds)
            const pendingAction = busy?.id === r.id ? busy.action : null
            return (
              <li key={r.id} className={styles.row}>
                <Chip variant="kind" icon={kind.icon} label={kind.label} />
                <Text tone="text" className={styles.grow}>
                  {r.name || r.kind}
                </Text>
                {outcome ? (
                  <Text variant="caption" tone="text-mute">
                    {threadCopy.resolve.outcome[outcome]}
                  </Text>
                ) : (
                  <Inline gap="space-8">
                    <Button
                      variant="outline"
                      size="sm"
                      ground="surface"
                      loading={pendingAction === 'keep'}
                      onClick={() => void act(r, 'keep')}
                    >
                      {threadCopy.resolve.keep}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      ground="surface"
                      loading={pendingAction === 'move'}
                      onClick={() => void act(r, 'move')}
                    >
                      {threadCopy.resolve.move}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      ground="surface"
                      loading={pendingAction === 'archive'}
                      onClick={() => void act(r, 'archive')}
                    >
                      {threadCopy.resolve.archive}
                    </Button>
                  </Inline>
                )}
              </li>
            )
          })}
        </ul>
      </Stack>
    </Modal>
  )
}
